import React, { useId } from 'react';
import { useTranslation } from '../../../../shared/i18n';

// Compact icons for stats bar
const CheckIconCompact = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#198754" viewBox="0 0 448 512">
    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
  </svg>
);

const FireIconCompact = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#fd7e14" viewBox="0 0 448 512">
    <path d="M159.3 5.4c7.8-7.3 19.9-7.2 27.7 .1c27.6 25.9 53.5 53.8 77.7 84c11-14.4 23.5-30.1 37-42.9c7.9-7.4 20.1-7.4 28 .1c34.6 33 63.9 76.6 84.5 118c20.3 40.8 33.8 82.5 33.8 111.9C448 404.2 348.2 512 224 512C98.4 512 0 404.1 0 276.5c0-38.4 17.8-85.3 45.4-131.7C73.3 97.7 112.7 48.6 159.3 5.4zM225.7 416c25.3 0 47.7-7 68.8-21c42.1-29.4 53.4-88.2 28.1-134.4c-4.5-9-16-9.6-22.5-2l-25.2 29.3c-6.6 7.6-18.5 7.4-24.7-.5c-16.5-21-46-58.5-62.8-79.8c-6.3-8-18.3-8.1-24.7-.1c-33.8 42.5-50.8 69.3-50.8 99.4C112 375.4 162.6 416 225.7 416z"/>
  </svg>
);

const BULLSEYE_PATH = "M448 256A192 192 0 1 0 64 256a192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 80a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-224a144 144 0 1 1 0 288 144 144 0 1 1 0-288zM224 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z";

const DailyTargetPie = ({ progress }) => {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
  const clipId = `dtp-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const size = 28;
  const center = size / 2;
  const radius = size / 2;
  const iconSize = 14;
  const iconOffset = (size - iconSize) / 2;
  const iconTransform = `translate(${iconOffset} ${iconOffset}) scale(${iconSize / 512})`;

  // Build a pie-slice path over the completed progress, sweeping clockwise from 12 o'clock.
  let wedgePath = null;
  if (clamped >= 1) {
    wedgePath = `M ${center} 0 A ${radius} ${radius} 0 1 1 ${center} ${size} A ${radius} ${radius} 0 1 1 ${center} 0 Z`;
  } else if (clamped > 0) {
    const angle = clamped * 360;
    const theta = (angle * Math.PI) / 180;
    const endX = (center + radius * Math.sin(theta)).toFixed(3);
    const endY = (center - radius * Math.cos(theta)).toFixed(3);
    const largeArc = angle > 180 ? 1 : 0;
    wedgePath = `M ${center} ${center} L ${center} 0 A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
    >
      {wedgePath && (
        <defs>
          <clipPath id={clipId}>
            <path d={wedgePath} />
          </clipPath>
        </defs>
      )}
      <circle cx={center} cy={center} r={radius} fill="#e5e7eb" />
      {wedgePath && <path d={wedgePath} fill="#667eea" />}
      <path d={BULLSEYE_PATH} transform={iconTransform} fill="#6c757d" />
      {wedgePath && (
        <g clipPath={`url(#${clipId})`}>
          <path d={BULLSEYE_PATH} transform={iconTransform} fill="#ffffff" />
        </g>
      )}
    </svg>
  );
};

// Format number with delimiter
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

/**
 * StatsBar - Compact stats display for activity session view
 * Shows: completed activities, current streak, and daily XP progress
 */
export const StatsBar = ({ child, stats, dailyTarget }) => {
  const { t } = useTranslation();

  return (
    <div className="card border-0 rounded-3 bg-white shadow-sm mb-4" data-testid="stats-bar">
      <div className="card-body py-2 px-3">
        <div className="d-flex flex-wrap align-items-center justify-content-center gap-3 gap-md-4">
          {/* Completed Activities */}
          <div className="d-flex align-items-center gap-2" data-testid="stats-bar-completed">
            <div className="bg-success-subtle rounded-2 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
              <CheckIconCompact />
            </div>
            <div>
              <span className="fw-bold" style={{ fontSize: '14px' }}>{stats.completed_sessions_count}</span>
              <span className="text-secondary ms-1" style={{ fontSize: '12px' }}>{t('children.dashboard.stats_bar.completed')}</span>
            </div>
          </div>

          {/* Separator */}
          <div className="vr d-none d-md-block" style={{ height: '24px' }}></div>

          {/* Current Streak */}
          <div className="d-flex align-items-center gap-2" data-testid="stats-bar-streak">
            <div className="bg-warning-subtle rounded-2 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
              <FireIconCompact />
            </div>
            <div>
              <span className="fw-bold" style={{ fontSize: '14px' }}>{child.current_streak}</span>
              <span className="text-secondary ms-1" style={{ fontSize: '12px' }}>{t('children.dashboard.stats_bar.streak_days')}</span>
            </div>
          </div>

          {/* Separator */}
          <div className="vr d-none d-md-block" style={{ height: '24px' }}></div>

          {/* Daily XP Target */}
          <div className="d-flex align-items-center gap-2" data-testid="stats-bar-daily-xp">
            <DailyTargetPie progress={dailyTarget.target_xp > 0 ? dailyTarget.achieved_xp / dailyTarget.target_xp : 0} />
            <div>
              <span className="fw-bold" style={{ color: '#667eea', fontSize: '14px' }}>
                {formatNumber(dailyTarget.achieved_xp)}
              </span>
              <span className="text-secondary" style={{ fontSize: '12px' }}>
                {' / '}{formatNumber(dailyTarget.target_xp)} XP
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
